import React, { useEffect, useState } from 'react';
import {
  Grid,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  InputAdornment,
  Paper,
  Button,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CardResumoTarefa from './components/CardResumoTarefa';
import { IBackupTarefaResumo, TipoTarefa } from './types';
import { CentralBackupsApi } from './services/centralBackupsApi';

interface ListaTarefasBackupProps {
  clienteID: string;
  token: string;
  onVerArquivos: (tarefaAgendadaID: string, nomeTarefa: string) => void;
  onVerLogs: (tarefaAgendadaID: string, nomeTarefa: string) => void;
  onBaixarUltimo: (arquivoID: string, nomeArquivo: string) => void;
  baixandoArquivoID: string | null;
}

export const ListaTarefasBackup: React.FC<ListaTarefasBackupProps> = ({
  clienteID,
  token,
  onVerArquivos,
  onVerLogs,
  onBaixarUltimo,
  baixandoArquivoID
}) => {
  const theme = useTheme();
  const [tarefas, setTarefas] = useState<IBackupTarefaResumo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Filtros
  const [filtroBusca, setFiltroBusca] = useState('');
  const [filtroGrupo, setFiltroGrupo] = useState('Todos');
  const [filtroTipo, setFiltroTipo] = useState<TipoTarefa | 'Todos'>('Todos');
  const [filtroStatus, setFiltroStatus] = useState<'Todos' | 'saudavel' | 'atencao' | 'critico'>('Todos');

  const carregarDados = () => {
    setCarregando(true);
    setErro(null);
    CentralBackupsApi.obterTarefas(clienteID, token)
      .then((data) => {
        setTarefas(data.items);
        setCarregando(false);
      })
      .catch((err) => {
        console.error("Erro ao carregar tarefas", err);
        setErro("Não foi possível carregar o resumo das tarefas de backup.");
        setCarregando(false);
      });
  };

  useEffect(carregarDados, [clienteID, token]);

  // Lista de grupos únicos para o dropdown
  const gruposDisponiveis = ['Todos', ...Array.from(new Set(tarefas.map(t => t.grupo || 'Sem Grupo').filter(Boolean)))];

  // Filtragem local
  const tarefasFiltradas = tarefas.filter((t) => {
    const atendeBusca = t.nome.toLowerCase().includes(filtroBusca.toLowerCase());
    const atendeGrupo = filtroGrupo === 'Todos' || (t.grupo || 'Sem Grupo') === filtroGrupo;
    const atendeTipo = filtroTipo === 'Todos' || t.tipo === filtroTipo;
    const atendeStatus = filtroStatus === 'Todos' || t.statusOperacional === filtroStatus;

    return atendeBusca && atendeGrupo && atendeTipo && atendeStatus;
  });

  const limparFiltros = () => {
    setFiltroBusca('');
    setFiltroGrupo('Todos');
    setFiltroTipo('Todos');
    setFiltroStatus('Todos');
  };

  return (
    <Box>
      {/* Barra de Filtros */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? '#0F172A' : 'rgba(0, 0, 0, 0.02)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '12px',
          p: 2.5,
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              placeholder="Buscar tarefa por nome..."
              value={filtroBusca}
              onChange={(e) => setFiltroBusca(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' }
              }}
            />
          </Grid>
          
          <Grid item xs={6} md={2.4}>
            <TextField
              select
              label="Grupo"
              value={filtroGrupo}
              onChange={(e) => setFiltroGrupo(e.target.value)}
              fullWidth
              size="small"
              InputProps={{ sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' } }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            >
              {gruposDisponiveis.map((grupo) => (
                <MenuItem key={grupo} value={grupo}>
                  {grupo}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} md={2.4}>
            <TextField
              select
              label="Tipo de Tarefa"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value as TipoTarefa | 'Todos')}
              fullWidth
              size="small"
              InputProps={{ sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' } }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            >
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="SQLServer">SQL Server</MenuItem>
              <MenuItem value="Arquivos">Arquivos</MenuItem>
              <MenuItem value="Bat">Script/BAT</MenuItem>
              <MenuItem value="Sybase">Sybase</MenuItem>
              <MenuItem value="Postgres">PostgreSQL</MenuItem>
              <MenuItem value="CheckDB">Diagnóstico de BD</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} md={1.8}>
            <TextField
              select
              label="Status"
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value as any)}
              fullWidth
              size="small"
              InputProps={{ sx: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(255, 255, 255, 0.8)' } }}
              InputLabelProps={{ sx: { color: 'text.secondary' } }}
            >
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="saudavel">Protegido</MenuItem>
              <MenuItem value="atencao">Atenção</MenuItem>
              <MenuItem value="critico">Crítico</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} md={1.4}>
            <Button
              onClick={limparFiltros}
              fullWidth
              variant="outlined"
              size="medium"
              startIcon={<CloseIcon />}
              sx={{
                borderColor: 'divider',
                color: 'text.secondary',
                textTransform: 'none',
                height: '40px',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(42, 81, 247, 0.04)',
                  color: 'primary.main'
                }
              }}
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Conteúdo Principal */}
      {carregando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {erro && !carregando && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {erro}
          </Typography>
          <Button variant="contained" onClick={carregarDados}>
            Tentar Novamente
          </Button>
        </Box>
      )}

      {!carregando && !erro && (
        <>
          {tarefasFiltradas.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: theme.palette.mode === 'dark' ? '#0F172A' : 'rgba(0, 0, 0, 0.02)',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '12px',
                p: 6,
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Nenhuma tarefa de backup corresponde aos filtros ativos.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3} alignItems="stretch">
              {tarefasFiltradas.map((tarefa) => (
                <Grid item xs={12} sm={6} lg={4} key={tarefa.tarefaAgendadaID} sx={{ display: 'flex' }}>
                  <CardResumoTarefa
                    tarefa={tarefa}
                    onVerArquivos={onVerArquivos}
                    onVerLogs={onVerLogs}
                    onBaixarUltimo={onBaixarUltimo}
                    baixandoArquivoID={baixandoArquivoID}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default ListaTarefasBackup;
